// QR code handoff — Phase 2 polish.
//
// "I want to show this exact view to the buyer who just sat down next to
// me" → tap 📱 → QR appears overlaying the panorama → buyer scans with
// their phone → lands on the same room/variant/yaw/pitch deep-link.
//
// Uses the existing URL hash sync (deepLinks) — the QR encodes
// `window.location.href` at the moment of capture. Re-tap or click the
// dimmed background to dismiss.

import QRCode from 'qrcode';
import { showToast } from './toast';

const OVERLAY_ID = 'qr-overlay';
const QR_PIXEL_SIZE = 512; // generous so phones can scan even at arm's length

export class QrPanel {
  private overlay: HTMLElement | null = null;

  /** Wire the topbar button to open the panel. */
  install(): void {
    const btn = document.getElementById('qr-button');
    if (!btn) {
      console.warn('[qr] #qr-button missing — skipping wire');
      return;
    }
    btn.addEventListener('click', () => void this.show(window.location.href));
    window.addEventListener('keydown', (e) => {
      // Q key opens the QR; Escape closes any open overlay.
      if (e.key === 'q' || e.key === 'Q') {
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        void this.show(window.location.href);
      } else if (e.key === 'Escape' && this.overlay) {
        this.hide();
      }
    });
  }

  /** Generate + display the QR code for the given URL. */
  async show(url: string): Promise<void> {
    this.hide(); // single instance at a time
    let dataUrl: string;
    try {
      dataUrl = await QRCode.toDataURL(url, {
        width: QR_PIXEL_SIZE,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: {
          dark: '#0a1620',     // dark brand backdrop
          light: '#ffffffff',  // pure white
        },
      });
    } catch (err) {
      console.error('[qr] generation failed', err);
      showToast('Falha ao gerar QR code', { tone: 'error' });
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'qr-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'QR code da vista atual');
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9000;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.78);
      backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
      cursor: pointer;
    `;
    overlay.innerHTML = `
      <div style="background: #ffffff; padding: 24px; border-radius: 12px; box-shadow: 0 12px 32px rgba(0,0,0,0.4); display: flex; flex-direction: column; align-items: center; gap: 12px;">
        <img alt="QR code da vista atual" src="${dataUrl}" style="width: 320px; height: 320px; display: block; image-rendering: pixelated;" />
        <p style="margin: 0; color: #0a1620; font-size: 14px; font-weight: 600; font-family: system-ui, sans-serif;">Escaneie para abrir esta vista no celular</p>
        <p style="margin: 0; color: #506070; font-size: 11px; max-width: 320px; word-break: break-all; text-align: center; font-family: system-ui, monospace;">${escapeHtml(url)}</p>
      </div>
      <p style="margin-top: 18px; color: rgba(255,255,255,0.78); font-size: 13px; font-family: system-ui, sans-serif;">Toque em qualquer lugar para fechar · Q para abrir · Esc para sair</p>
    `;
    overlay.addEventListener('click', () => this.hide());
    document.body.appendChild(overlay);
    this.overlay = overlay;
  }

  hide(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
